import { produce } from "immer"
import { MATRIX_AXIS_POSITIONS } from "./Constants"
import { ProductDto, ProductDtoAttributes } from "./Product"
import {
    MATRIX_AXIS_TYPE, MATRIX_ITEM_STATUS, MATRIX_ITEM_UPDATE_OPTION,
    MatrixItemPayload,
    MatrixItemUpdatePayload,
    ProductUpdatePayload
} from "./Types"
import { ProvisionMatrixItems } from "./Utils"
import { MatrixAxis } from "./matrix/Matrix"
import { ProductInitialState } from "./reducer"
import { InventoryData, InventoryLocationQuantities } from "../inventory/data/model/inventoryData"

export const ProductStateHandler = (state: ProductInitialState) => {
    return {

        updateProduct(payload: ProductUpdatePayload): ProductDtoAttributes {
            const updatedState = produce(state, draft => {
                // @ts-ignore
                draft[payload.updateType] = payload.value
            })
            return produce(updatedState, draft => {
                if (draft.matrix) {
                    draft.matrix.attributes.items = ProvisionMatrixItems(updatedState, false)
                    draft.matrix.attributes.didUpdate_ = true
                }
            })
        },

        initialMatrixProvision(payload: ProductDtoAttributes): ProductDtoAttributes {
            return produce(payload, draft => {
                if (draft.matrix) {
                    draft.matrix.attributes.items = ProvisionMatrixItems(payload, true)
                }
            })
        },

        set(payload: ProductDto): ProductInitialState {
            return {
                id: payload.id,
                ...payload.attributes,
            }
        },

        update(payload: Partial<ProductDtoAttributes>): ProductInitialState {
            return {
                ...state,
                ...payload,
            }
        },

        updateMatrixAxis(payload: MatrixAxis): ProductDtoAttributes {
            const index = state.matrix.attributes.axes.findIndex(a => a.type === payload.type)
            let updatedState: ProductDtoAttributes
            if (index === -1) {
                updatedState = produce(state, draft => {
                    draft.matrix.attributes.axes.push(payload)
                })
            } else {
                updatedState = produce(state, draft => {
                    draft.matrix.attributes.axes[index] = payload
                })
            }
            return produce(updatedState, draft => {
                draft.matrix.attributes.items = ProvisionMatrixItems(updatedState, false)
                draft.matrix.attributes.didUpdate_ = true
            })
        },

        deleteMatrixAxis(payload: MATRIX_AXIS_TYPE): ProductDtoAttributes {
            const index = state.matrix.attributes.axes.findIndex(a => a.type === payload)
            if (index === -1) return state
            const updatedState = produce(state, draft => {
                draft.matrix.attributes.didUpdate_ = true
                const filteredAxes = state.matrix.attributes.axes.filter(axis => axis.type !== payload)

                draft.matrix.attributes.axes = filteredAxes.map((axis, index) => {
                    return produce(axis, axisDraft => {
                        axisDraft.type = MATRIX_AXIS_POSITIONS[index]
                        axisDraft.options = axis.options?.map(option => {
                            return produce(option, optionDraft => {
                                optionDraft.attributes.axisType = MATRIX_AXIS_POSITIONS[index]
                            })
                        })
                    })
                })
            })

            return produce(updatedState, draft => {
                draft.matrix.attributes.items = ProvisionMatrixItems(updatedState, false)
            })
        },


        updateMatrixItem(payload: MatrixItemUpdatePayload) {
            const index = state.matrix.attributes.items.findIndex(item => item.id === payload.id)
            if (payload.updateType != MATRIX_ITEM_UPDATE_OPTION.MULTI_CURRENCY_PRICING &&
                state.matrix.attributes.items[index].attributes.product.attributes[payload.updateType] === payload.value) {
                return state
            }

            return produce(state, draft => {
                draft.matrix.attributes.didUpdate_ = true
                const item = draft.matrix.attributes.items[index]
                draft.matrix.attributes.items[index] = produce(item, draft => {
                    if (item.attributes.status_ != MATRIX_ITEM_STATUS.NEW) {
                        draft.attributes.status_ = MATRIX_ITEM_STATUS.UPDATED
                    }
                    switch (payload.updateType) {
                    case MATRIX_ITEM_UPDATE_OPTION.MULTI_CURRENCY_PRICING:
                        const index = draft.attributes.product.attributes[payload.updateType].findIndex(mc => {
                            // @ts-ignore todo to update when global state is split and simplified
                            return mc.attributes ? mc.attributes.currency === payload.value.currency :
                            // @ts-ignore
                                mc.currency === payload.value.currency
                        })
                        if (index === -1) {
                            draft.attributes.product.attributes.multiCurrencyPricing.push(payload.value)
                            break
                        }
                        draft.attributes.product.attributes.multiCurrencyPricing[index] = payload.value
                        break
                    default:
                        // @ts-ignore
                        draft.attributes.product.attributes[payload.updateType] = payload.value

                    }
                })
            })
        },

        deleteMatrixItem(payload: MatrixItemPayload) {
            const index = state.matrix.attributes.items.findIndex(item => item.id === payload.id)
            if (index === -1) return state
            return produce(state, draft => {
                draft.matrix.attributes.didUpdate_ = true
                draft.matrix.attributes.items[index].attributes.deleted = true
            })
        },

        restoreMatrixItem(payload: MatrixItemPayload) {
            const index = state.matrix.attributes.items.findIndex(item => item.id === payload.id)
            if (index === -1) return state
            return produce(state, draft => {
                draft.matrix.attributes.didUpdate_ = true
                draft.matrix.attributes.items[index].attributes.deleted = false
            })
        },

        setInventoryData(payload: InventoryData): ProductDtoAttributes {
            const s = produce(state, draft => {
                const dataIndex = draft.inventoryData.findIndex(
                    (d: InventoryData) => d.id == payload.id
                )
                if (dataIndex != -1) {
                    draft.inventoryData[dataIndex] = payload
                }
                const inventoryLocationQuantitiesIndex = draft.inventoryLocationQuantities.findIndex(
                    (q: InventoryLocationQuantities) => q.id == payload.attributes.inventoryLocationQuantities.id
                )
                if (inventoryLocationQuantitiesIndex != -1) {
                    draft.inventoryLocationQuantities[inventoryLocationQuantitiesIndex] = payload.attributes.inventoryLocationQuantities
                }
            })
            return s
        }
        
    }
}







// WEBPACK FOOTER //
// ./src/state/product/ProductStateHandler.ts